import api from 'application/utilities/axiosIntance';
import Button from 'presentation/components/Button/Button';
import InputField from 'presentation/components/InputField/InputField';
import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from "./ResetPassword.module.scss";
import { ResetPasswordResponse, ResetPasswordSuccessResult } from 'application/types/ResetPasswordResponse';
import { Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { setRegion } from 'features/region/regionSlice'
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ password: '', confirmPassword: "" });
  const [message, setMessage] = useState<React.ReactNode>("");
  const [apiError, setApiError] = useState("");
  const [userData, setUserData] = useState<ResetPasswordSuccessResult | null>(null);
  const [expired, setExpired] = useState(false);
  const [dataLoader, setDataLoader] = useState(true);
  const [submit, setSubmit] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [code, setCode] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get("code");

    if (!code) {
      setApiError(t("INVALID_RESET_LINK"));
      setDataLoader(false);
      return;
    }
    setCode(code);

    api.get<ResetPasswordResponse>(`/Login/ResetPassword?code=${code}`)
      .then((res) => {
        const { isSuccess, result } = res.data;

        if (isSuccess) {
          const successResult = result as ResetPasswordSuccessResult;

          if (successResult.urlStatus === "Expired") {
            dispatch(setRegion(successResult.regionCode));
            setExpired(true);
          } else if (successResult.urlStatus === "Invalid") {
            setApiError(t("RESET_LINK_EXPIRED"));
          }
          else {
            setUserData(successResult);
            dispatch(setRegion(successResult.regionCode));
            setApiError('');
            i18n.changeLanguage(successResult.regionCode.toLowerCase());
          }
        }
      })
      .catch((error) => {
        setApiError(t("GENERIC_SOMETHING_WENT_WRONG"));
      })
      .finally(() => {
        setDataLoader(false);
      });;

  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setMessage("");
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setSubmit(false);
    setButtonLoader(true);
    const { password, confirmPassword } = formData;

    if (!password.trim() || !confirmPassword.trim()) {
      setMessage(t("FIELD_REQUIRED_PASSWORD"));
      setButtonLoader(false);
      return;
    }

    if (password !== confirmPassword) {
      setMessage(t("PASSWORDS_MISMATCH"));
      setButtonLoader(false);
      return;
    }


    const authHeader = `Basic ${btoa(`${password}`)}`;

    api.post("/Login/ResetPasswordDetails", {
      token: code,
    },
      {
        headers: {
          Authorization: authHeader,
        }
      })
      .then((response) => {
        const { isSuccess, result, errorMessage } = response.data;
        if (isSuccess) {
          setSubmit(true);
          setMessage(result?.responseMessage);
          setApiError("");
        } else {
          if (errorMessage) {
            const errors = errorMessage.split("\r\n");
            setPasswordErrors(errors);
            setMessage(
              /* jsx element kept in single line for proper space alignment */
              <div>{t("PASSWORD_REQUIREMENT_MESSAGE_PREFIX")}<span className={styles.link} onClick={() => setShowPasswordRequirements(true)}>{t("PASSWORD_REQUIREMENT_MESSAGE_LINK")}</span>{t("PASSWORD_REQUIREMENT_MESSAGE_SUFFIX")}</div> 
            );
          } else {
            setMessage(result?.responseMessage);
          }
        }
      })
      .catch(() => {
        setMessage(t("GENERIC_UNEXPECTED_ERROR"));
      })
      .finally(() => {
        setButtonLoader(false);
      });
  };

  if (showPasswordRequirements) {
    return (
      <div className={styles.popupOverlay}>
        <div className={styles.popupContent}>
          <div className={styles.passwordInfoHeading}>{t("PASSWORD_REQUIREMENTS")}</div>
          <div className={`${styles.passwordInfoDescription} ms-0`}>{t("PASSWORD_REQUIREMENTS_LIST_HEADING")}</div>
          {passwordErrors.map((err, index) => (
            <div className={styles.errors} key={index}>{err}</div>
          ))}
          <Button label="OK" onClick={() => setShowPasswordRequirements(false)} />
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex justify-content-center align-items-center w-100 position-relative">

      {dataLoader && (
        <div className="position-absolute top-50 start-50 translate-middle">
          <Spinner animation="border" variant="light" />
        </div>
      )}

      {!dataLoader && (
        <nav className={`${styles.popup} px-5 py-4 mb-2 `}>
          {apiError && (
            <div className={`${styles.error} d-flex align-items-center justify-content-center pt-2 ps-1 mb-3`}>
              {apiError}
            </div>
          )}

          {expired && (
            <div>
              <div className={`${styles.heading} pt-3 text-center`}>
                {t("RESET_LINK_EXPIRED")}
              </div>
              <Link to="/" className="linkText d-flex justify-content-center pt-3">
                {t("RETURN_MESSAGE")}
              </Link>
            </div>
          )}

          {!apiError && !expired && !submit && (
            <>
              <div className="d-flex align-items-center justify-content-center">
                <div>
                  <div className={`${styles.heading} pe-1`}>
                    {t("RESET_PASSWORD_HEADING")}
                  </div>
                  <div className={`${styles.description} pe-1`}>
                    {t("RESET_PASSWORD_INSTRUCTION")}
                  </div>
                </div>
              </div>

              {message && (
                <div className="validationError d-flex align-items-center justify-content-center pt-2 ps-1 text-center">
                  {message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <InputField containerClass={styles.readOnly} readOnly name="userName" value={userData?.userName} />
                <InputField containerClass={styles.readOnly} readOnly name="email" value={userData?.email} />
                <InputField type="password" maxLength={50} name="password" value={formData.password} onChange={handleChange} placeholder={t("FIELD_NAME_NEW_PASSWORD")} autoFocus />
                <InputField type="password" maxLength={50} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder={t("FIELD_NAME_CONFIRM_PASSWORD")} />
                <Button loading={buttonLoader} type="submit" label={t("BUTTON_SUBMIT")} />
                <Button label={t("BUTTON_CANCEL")} onClick={() => navigate("/")} />
              </form>
            </>
          )}

          {submit && (
            <div className="align-items-center justify-content-center">
              <div className={`${styles.info} pt-3 text-center`}>
                {t("PASSWORD_RESET")}
              </div>
              <Link to="/" className="linkText d-flex justify-content-center pt-3">
                {t("RETURN_MESSAGE")}
              </Link>
            </div>
          )}
        </nav>
      )}
    </div>
  );
}

export default ResetPassword;
